import { queryOptions } from '@tanstack/react-query';
import { getAreaDescendants, getAreas } from '@/actions/areas';
import { AreaType } from '@/types/booliApi';

const DEFAULT_SUBURB_LOCALITY = [1, 2, 20, 22, 23, 35, 64, 78, 95, 97, 118, 231, 253, 315, 393, 419, 424] as const;

const RESOURCE = 'areas' as const;

const areas = {
  byId: ({ ids }: { ids: number[] }) => {
    const sortedIds = [...new Set(ids)].sort((a, b) => a - b);
    return queryOptions({
      queryFn: () => getAreas({ ids: sortedIds }),
      queryKey: [RESOURCE, 'byId', ...sortedIds] as const,
      enabled: sortedIds.length > 0,
    });
  },
  search: <T extends boolean = false>({
    q,
    includeSuggestion = false as T,
    types = [],
  }: {
    q: string;
    includeSuggestion?: T;
    types?: AreaType[];
  }) => {
    const sortedTypes = [...types].sort();
    return queryOptions({
      queryFn: () => getAreas({ q, includeSuggestion, types: sortedTypes }),
      queryKey: [RESOURCE, 'search', { includeSuggestion }, ...sortedTypes, q] as const,
    });
  },
  counties: () =>
    queryOptions({
      queryFn: () => getAreas({ types: [AreaType.County] }),
      queryKey: [RESOURCE, 'counties', AreaType.County] as const,
    }),
  suburb: <T extends boolean = false>({ includeSuggestion = false as T }: { includeSuggestion?: T }) =>
    queryOptions({
      queryFn: () =>
        getAreas({
          ids: [...DEFAULT_SUBURB_LOCALITY],
          includeSuggestion: includeSuggestion,
        }),
      queryKey: [RESOURCE, 'suburb', AreaType.Suburb, { includeSuggestion }] as const,
    }),
  parent: ({ id }: { id: number }) =>
    queryOptions({
      queryFn: () => getAreas({ ids: [id] }),
      queryKey: [RESOURCE, 'parent', id] as const,
    }),
  byType: ({ type }: { type: AreaType }) =>
    queryOptions({
      queryFn: () => getAreas({ types: [type] }),
      queryKey: [RESOURCE, 'byType', type] as const,
    }),
  descendents: ({ areaParent, type }: { areaParent: number; type: AreaType }) =>
    queryOptions({
      queryFn: () => getAreaDescendants(areaParent, type),
      queryKey: [RESOURCE, 'descendents', areaParent, type] as const,
    }),
  byCoordinates: <T extends boolean = false>({
    lat,
    lng,
    includeSuggestion = false as T,
  }: {
    lat: number;
    lng: number;
    includeSuggestion?: T;
  }) =>
    queryOptions({
      queryFn: () => getAreas({ lat, lng, includeSuggestion }),
      queryKey: [RESOURCE, 'coordinates', { lat, lng }, { includeSuggestion }] as const,
    }),
};

export { areas, RESOURCE as AREAS_QUERY_KEY };
